import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import { Button, Buttons, CancelButton, DateTime, Drawer, FormMessage, Icon, Tooltip } from '~/components';
import { useApi, useWorkspace } from '~/contexts';
import { colors, weights } from '~/styles';
import { dateFormats } from '~/utils';

const HistoryTable = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1.1fr) minmax(0, 1.9fr) minmax(0, 1.8fr) minmax(0, 0.8fr) minmax(0, 1.1fr);
  row-gap: 1px;
  width: calc(100% + 3rem);
  margin: 0 -1.5rem;
  background-color: ${colors.grey10};
`;

const Label = styled.p`
  margin: 0;
  padding-bottom: 0.75rem;
  font-size: 0.875rem;
  font-weight: ${weights.bold};
  text-align: ${({ align }) => (align === 'right' ? 'right' : 'left')};
  background-color: ${colors.white};

  &:nth-child(5n + 1) {
    padding-left: 1.5rem;
  }

  &:nth-child(5n) {
    padding-right: 1.5rem;
  }
`;

const Data = styled.div`
  display: flex;
  justify-content: ${({ align }) => (align === 'right' ? 'flex-end' : 'flex-start')};
  padding: 0.5rem 0;
  background-color: ${({ $isPending }) => ($isPending ? colors.warning10 : colors.white)};

  &:nth-child(5n + 1) {
    padding-left: 1.5rem;
  }

  &:nth-child(5n) {
    padding-right: 1.5rem;
  }
`;

const TooltipData = styled(Data)`
  height: 100%;
`;

const Strong = styled.span`
  font-weight: ${weights.bold};
`;

const SeatValue = styled(Strong)``;

const RetryButton = styled(Button)`
  align-self: flex-start;
  margin-top: 1rem;
  color: ${colors.grey40};
  background-color: ${colors.grey5};

  &:hover {
    color: ${colors.grey55};
    background-color: ${colors.grey10};
  }
`;

const Message = styled.div`
  padding: 0.75rem 1rem;
  color: ${colors.grey55};
  background-color: ${colors.grey5};
  border-radius: 0.3125rem;
`;

const DataContent = styled.div`
  min-width: 0;
  white-space: normal;
  overflow-wrap: anywhere;
  font-size: 0.875rem;
  line-height: 1.2rem;
  text-align: ${({ align }) => (align === 'right' ? 'right' : 'left')};
`;

const ChangeTypeContent = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 0.375rem;
`;

const ChangeTypeText = styled.span`
  min-width: 0;
  white-space: normal;
  overflow-wrap: anywhere;
`;

const SourceWorkspaceText = styled.span`
  color: ${colors.grey55};
`;

function formatChangeType(type) {
  return (
    {
      create: 'Member Created',
      delete: 'Member Deleted',
      activated: 'Member Activated',
      deactivated: 'Member Deactivated',
      billable_enabled: 'Set to Billable',
      billable_disabled: 'Set to Non-Billable',
    }[type] || type
  );
}

function getChangeTypeIcon(changeTypes = []) {
  if (changeTypes.includes('delete')) {
    return { icon: 'times', color: colors.error };
  }

  if (changeTypes.includes('create')) {
    return { icon: 'plus', color: colors.success };
  }

  return { icon: 'pencil-alt', color: colors.grey40, style: { fontSize: '0.675rem' } };
}

function formatCount(value) {
  return value === null || value === undefined ? 'N/A' : value;
}

function formatProcessingStatus(status) {
  return (
    {
      pending: 'Pending',
      processed: 'Processed',
    }[status] || status
  );
}

function formatChangeContextTooltip(event) {
  const oldActive = event.oldValues?.isActive;
  const newActive = event.newValues?.isActive;
  const oldBillable = event.oldValues?.isBillable;
  const newBillable = event.newValues?.isBillable;
  const isCreate = (event.changeTypes || []).includes('create');
  const isDelete = (event.changeTypes || []).includes('delete');

  if (isDelete) {
    const activeState = oldActive === true ? 'active' : 'inactive';
    const billableState = oldBillable === true ? 'billable' : 'not billable';

    return (
      <>
        <div>
          <Strong>What Changed?</Strong>
        </div>
        <div>
          The member was deleted. Previous status was {activeState} and {billableState}.
        </div>
      </>
    );
  }

  if (isCreate) {
    const activeState = newActive === true ? 'active' : 'inactive';
    const billableState = newBillable === true ? 'billable' : 'not billable';

    return (
      <>
        <div>
          <Strong>What Changed?</Strong>
        </div>
        <div>
          The member was created. Current status is {activeState} and {billableState}.
        </div>
      </>
    );
  }

  const changes = [];
  if (oldActive !== newActive) {
    if (newActive === true) changes.push('active');
    else if (newActive === false) changes.push('inactive');
  }

  if (oldBillable !== newBillable) {
    if (newBillable === true) changes.push('billable');
    else if (newBillable === false) changes.push('not billable');
  }

  let detail = 'The member was updated. This change did not affect the chargeable member count.';
  if (changes.length === 1) {
    detail = `The member was set to ${changes[0]}.`;
  } else if (changes.length === 2) {
    detail = `The member was set to ${changes[0]} and ${changes[1]}.`;
  }

  return (
    <>
      <div>
        <Strong>What Changed?</Strong>
      </div>
      <div>{detail}</div>
    </>
  );
}

function seatTotalTooltip(event) {
  const total = event.seatTotal ?? event.computedMemberCount ?? 'Not set';
  const billable = event.activeBillableCount;
  const active = event.activeMemberCount;
  const nonbillFloorCandidate = event.nonbillFloorCandidate ?? null;
  const appliedFloorReason = event.appliedFloorReason;
  const chargeableMemberLabel = Number(total) === 1 ? 'chargeable member' : 'chargeable members';
  const activeBillableMemberLabel = Number(billable) === 1 ? 'active billable member' : 'active billable members';

  let seatCountExplanation = (
    <div>
      You have <Strong>{billable}</Strong> {activeBillableMemberLabel}.
    </div>
  );

  if (appliedFloorReason === 'min_members') {
    seatCountExplanation = (
      <div>
        You currently have <Strong>{billable ?? 'Not set'}</Strong> {activeBillableMemberLabel}. Your workspace is set
        to require a minimum of <Strong>{total}</Strong> {chargeableMemberLabel}.
      </div>
    );
  } else if (appliedFloorReason === 'limit_nonbill') {
    if (
      active !== null &&
      active !== undefined &&
      nonbillFloorCandidate !== null &&
      billable !== null &&
      billable !== undefined
    ) {
      const memberLabel = Number(active) === 1 ? 'active member' : 'active members';
      seatCountExplanation = (
        <div>
          Your workspace has <Strong>{active}</Strong> {memberLabel}, and half of that (rounded up) is{' '}
          <Strong>{nonbillFloorCandidate}</Strong>, which is higher than your {activeBillableMemberLabel} at{' '}
          <Strong>{billable}</Strong>. This sets your chargeable member total to <Strong>{total}</Strong>.
        </div>
      );
    } else {
      seatCountExplanation = (
        <div>
          Your chargeable member total is <Strong>{total}</Strong>.
        </div>
      );
    }
  }

  if (event.seatTotalIsEstimated) {
    seatCountExplanation = (
      <>
        {seatCountExplanation}
        <br />
        <div>
          <em>This chargeable member total is estimated and will be confirmed when the changes are finalized.</em>
        </div>
      </>
    );
  }

  return (
    <>
      <div>
        <Strong>Chargeable Members Explanation</Strong>
      </div>
      {seatCountExplanation}
    </>
  );
}

function chargeableMembersChangeTooltip(event) {
  const before = event.seatCountBefore;
  const after = event.seatCountAfter;
  const hasStripeChangeData = before !== null && before !== undefined && after !== null && after !== undefined;

  if (!hasStripeChangeData || event.seatTotalIsEstimated) {
    return null;
  }

  if (before === after) {
    return (
      <div>
        Your billed chargeable member count remained at <Strong>{after}</Strong> because it already matched the current
        chargeable member total.
      </div>
    );
  }

  return (
    <div>
      Your billed chargeable member count changed from <Strong>{before}</Strong> to <Strong>{after}</Strong>.
    </div>
  );
}

function statusTooltip(event) {
  if (!event.processedAt) {
    return 'This event has not been processed yet.';
  }

  const chargeableMembersChange = chargeableMembersChangeTooltip(event);

  return (
    <>
      Processed on: <DateTime value={event.processedAt} format={dateFormats.compactDate} /> at{' '}
      <DateTime value={event.processedAt} format={dateFormats.compactTime} />
      {chargeableMembersChange && (
        <>
          <br />
          <br />
          <div>
            <Strong>Chargeable Members Change</Strong>
          </div>
          {chargeableMembersChange}
        </>
      )}
    </>
  );
}

export default function ViewBillingHistory({ onClose }) {
  const api = useApi();
  const { workspace } = useWorkspace();
  const drawerRef = useRef(null);

  const [events, setEvents] = useState([]);
  const [page, setPage] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [error, setError] = useState(null);

  const loadEvents = useCallback(
    async (nextPage, append = false) => {
      if (append) setIsLoadingMore(true);
      else setIsLoading(true);
      setError(null);
      try {
        const { data } = await api.www.workspaces(workspace.id).billing.getEvents({ page: nextPage, size: 50 });
        const incoming = data?.results || [];
        setHasMore(Boolean(data?.hasMore));
        setPage(nextPage);
        setEvents((current) => {
          const byId = new Map((append ? current : []).map((item) => [item.id, item]));
          for (const item of incoming) byId.set(item.id, item);
          return Array.from(byId.values());
        });
      } catch (fetchError) {
        setError(fetchError?.message || 'Unable to load billing history.');
      } finally {
        setIsLoading(false);
        setIsLoadingMore(false);
      }
    },
    [api, workspace.id],
  );

  useEffect(() => {
    loadEvents(0, false);
  }, [loadEvents]);

  useEffect(() => {
    const el = drawerRef.current;
    if (!el) return undefined;

    const onScroll = () => {
      if (isLoading || isLoadingMore || !hasMore || error) return;
      const threshold = 200;
      const distanceFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
      if (distanceFromBottom <= threshold) {
        loadEvents(page + 1, true);
      }
    };

    el.addEventListener('scroll', onScroll);
    return () => el.removeEventListener('scroll', onScroll);
  }, [error, hasMore, isLoading, isLoadingMore, loadEvents, page]);

  const isEmpty = useMemo(() => !isLoading && !error && events.length === 0, [events.length, error, isLoading]);

  return (
    <Drawer ref={drawerRef} isOpen title="Billing History" onClose={onClose}>
      {(closeDrawer) => (
        <>
          {error && (
            <>
              <FormMessage.Error>{error}</FormMessage.Error>
              <RetryButton onClick={() => loadEvents(events.length ? page : 0, events.length > 0)}>Retry</RetryButton>
            </>
          )}
          {isEmpty && <Message>No billing history events are available yet.</Message>}
          {isLoading && <Message>Loading billing history...</Message>}
          {!!events.length && (
            <HistoryTable>
              <Label>Event Time</Label>
              <Label>Member</Label>
              <Label>Change Type</Label>
              <Label align="right">Chargeable Members</Label>
              <Label align="right">Status</Label>
              {events.map((event) => (
                <React.Fragment key={event.id}>
                  {(() => {
                    const isPending = event.processingStatus === 'pending';

                    return (
                      <>
                        <Data $isPending={isPending}>
                          <DataContent>
                            <DateTime value={event.createdAt} format={dateFormats.compactDate} />
                            <br />
                            <DateTime value={event.createdAt} format={dateFormats.compactTime} />
                          </DataContent>
                        </Data>
                        <Data $isPending={isPending}>
                          <DataContent>
                            <Strong>{event.memberName || 'Unknown'}</Strong>
                            <br />
                            <span>{event.memberEmail || 'N/A'}</span>
                            {event.sourceWorkspaceName && (
                              <>
                                <br />
                                <SourceWorkspaceText>Workspace: {event.sourceWorkspaceName}</SourceWorkspaceText>
                              </>
                            )}
                          </DataContent>
                        </Data>
                        <Tooltip
                          message={formatChangeContextTooltip(event)}
                          Container={TooltipData}
                          $isPending={isPending}>
                          <DataContent>
                            <ChangeTypeContent>
                              <Icon {...getChangeTypeIcon(event.changeTypes || [])} />
                              <ChangeTypeText>
                                {(event.changeTypes || []).map(formatChangeType).join(', ') || 'Change event'}
                              </ChangeTypeText>
                            </ChangeTypeContent>
                          </DataContent>
                        </Tooltip>
                        {event.seatTotal === null || event.seatTotal === undefined ? (
                          <Data align="right" $isPending={isPending}>
                            <DataContent align="right">
                              <SeatValue>Pending</SeatValue>
                            </DataContent>
                          </Data>
                        ) : (
                          <Tooltip
                            message={seatTotalTooltip(event)}
                            Container={TooltipData}
                            align="right"
                            $isPending={isPending}>
                            <DataContent align="right">
                              <SeatValue>{formatCount(event.seatTotal)}</SeatValue>
                            </DataContent>
                          </Tooltip>
                        )}
                        <Tooltip
                          message={statusTooltip(event)}
                          Container={TooltipData}
                          align="right"
                          $isPending={isPending}>
                          <DataContent align="right">
                            <Strong>{formatProcessingStatus(event.processingStatus)}</Strong>
                          </DataContent>
                        </Tooltip>
                      </>
                    );
                  })()}
                </React.Fragment>
              ))}
            </HistoryTable>
          )}
          {isLoadingMore && <Message>Loading more...</Message>}
          <Drawer.Actions>
            <Buttons align="right">
              <CancelButton onClick={closeDrawer}>Close</CancelButton>
            </Buttons>
          </Drawer.Actions>
        </>
      )}
    </Drawer>
  );
}
